/**
 * Snooze picker — first-cut version with presets + native datetime input.
 *
 * The roadmap calls for a full month-grid calendar via `react-day-picker`,
 * but presets cover ~90% of usage and the native `<input type="datetime-local">`
 * fallback handles the rest without a heavy dependency. We can swap in
 * react-day-picker as a drop-in replacement for the custom-date row later.
 *
 * Sends `wake_at` (unix epoch) directly to the backend. Times are computed
 * in the user's local timezone from `Date` natively — Todoist sync uses
 * Europe/Dublin per Mark.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { format } from 'date-fns'
import { clsx } from 'clsx'
import { Clock, X } from 'lucide-react'

type Props = {
  onPick: (wakeAtUnix: number) => void
  onCancel: () => void
  /**
   * Optional override for the small caption under the custom-date input.
   * Defaults to the Todoist-sync description; email-snooze callers pass an
   * Outlook-specific note instead so the picker doesn't lie about what
   * happens behind the scenes.
   */
  footerNote?: string
  title?: string
  presets?: SnoozePreset[]
}

export type SnoozePreset = { label: string; compute: () => Date }

const PRESETS: SnoozePreset[] = [
  {
    label: 'Tomorrow',
    compute: () => new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
  },
  {
    label: 'In 2 days',
    compute: () => new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
  },
  {
    label: 'In 3 days',
    compute: () => new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
  },
  {
    label: 'In 5 days',
    compute: () => new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
  },
  {
    label: 'Next Saturday 9:00',
    compute: () => {
      const d = new Date()
      const day = d.getDay() // 0 Sun … 6 Sat
      const daysUntilSat = (6 - day + 7) % 7 || 7
      d.setDate(d.getDate() + daysUntilSat)
      d.setHours(9, 0, 0, 0)
      return d
    },
  },
  {
    label: 'Next Sunday 9:00',
    compute: () => {
      const d = new Date()
      const day = d.getDay()
      const daysUntilSun = (7 - day) % 7 || 7
      d.setDate(d.getDate() + daysUntilSun)
      d.setHours(9, 0, 0, 0)
      return d
    },
  },
  {
    label: 'Next Monday 9:00',
    compute: () => {
      const d = new Date()
      const day = d.getDay()
      const daysUntilMon = (8 - day) % 7 || 7
      d.setDate(d.getDate() + daysUntilMon)
      d.setHours(9, 0, 0, 0)
      return d
    },
  },
  {
    label: 'In 1 week',
    compute: () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  },
  {
    label: 'In 2 weeks',
    compute: () => new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
  },
  {
    label: 'In 3 weeks',
    compute: () => new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
  },
  {
    label: 'In 1 month',
    compute: () => {
      const d = new Date()
      d.setMonth(d.getMonth() + 1)
      return d
    },
  },
  {
    label: 'In 2 months',
    compute: () => {
      const d = new Date()
      d.setMonth(d.getMonth() + 2)
      return d
    },
  },
  {
    label: 'In 3 months',
    compute: () => {
      const d = new Date()
      d.setMonth(d.getMonth() + 3)
      return d
    },
  },
  {
    label: 'In 6 months',
    compute: () => {
      const d = new Date()
      d.setMonth(d.getMonth() + 6)
      return d
    },
  },
]

export const LANE_SNOOZE_PRESETS: SnoozePreset[] = [
  {
    label: 'In 1 hour',
    compute: () => new Date(Date.now() + 60 * 60 * 1000),
  },
  {
    label: 'In 2 hours',
    compute: () => new Date(Date.now() + 2 * 60 * 60 * 1000),
  },
  {
    label: 'In 4 hours',
    compute: () => new Date(Date.now() + 4 * 60 * 60 * 1000),
  },
  {
    label: 'In 8 hours',
    compute: () => new Date(Date.now() + 8 * 60 * 60 * 1000),
  },
  ...PRESETS,
]

function isoLocal(d: Date): string {
  // datetime-local needs `YYYY-MM-DDTHH:MM` with NO offset
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

export default function SnoozePicker({
  onPick,
  onCancel,
  footerNote,
  title,
  presets = PRESETS,
}: Props) {
  const [customDt, setCustomDt] = useState<string>(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(9, 0, 0, 0)
    return isoLocal(tomorrow)
  })

  function pick(d: Date) {
    const wakeAt = Math.floor(d.getTime() / 1000)
    if (wakeAt <= Math.floor(Date.now() / 1000)) {
      // Defensive: don't allow past times
      return
    }
    onPick(wakeAt)
  }

  function pickCustom() {
    if (!customDt) return
    const d = new Date(customDt)
    pick(d)
  }

  const modal = (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="max-h-[calc(100dvh-2rem)] w-full max-w-sm overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-stone-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200 dark:border-stone-700">
          <Clock className="w-4 h-4 text-blue-600" />
          <h3 className="font-semibold flex-1">{title || 'Snooze topic'}</h3>
          <button onClick={onCancel} className="text-stone-400 hover:text-stone-600 p-1" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 grid grid-cols-2 gap-1.5">
          {presets.map((p) => {
            const d = p.compute()
            return (
              <button
                key={p.label}
                onClick={() => pick(d)}
                className={clsx(
                  'text-left px-3 py-2 rounded-xl border border-stone-200 dark:border-stone-600',
                  'hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300',
                  'transition-colors',
                )}
              >
                <div className="font-medium text-sm">{p.label}</div>
                <div className="text-[11px] text-stone-500">
                  {format(d, 'EEE MMM d, HH:mm')}
                </div>
              </button>
            )
          })}
        </div>

        <div className="border-t border-stone-200 dark:border-stone-700 p-3">
          <label className="block text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">
            Custom date &amp; time
          </label>
          <div className="flex gap-2">
            <input
              type="datetime-local"
              value={customDt}
              onChange={(e) => setCustomDt(e.target.value)}
              className="flex-1 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={pickCustom}
              disabled={!customDt}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-stone-300 dark:disabled:bg-stone-700 disabled:text-stone-500"
            >
              Snooze
            </button>
          </div>
          <p className="text-[10px] text-stone-400 mt-1.5">
            {footerNote ?? 'Also updates the source Todoist task\'s due date and moves it to "Pending".'}
          </p>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}
